<script setup lang="ts">
import {
  IconImages,
  LightBox,
  UserGalleryGrid,
  UserGalleryGridItem
} from '@hobbii/hobbii-ui';
import { computed, ref, watch } from 'vue';

import { KindType, Post } from '@/__generated__/gateway/graphql';
import {
  useCommunityAuth,
  useContentModel
} from '@/components/community/utils/composables.ts';
import { ContentModel } from '@/components/community/utils/types';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import LoadingSpinner from '@/shared/components/UI/LoadingSpinner.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import { useCommunityPosts } from '@/shared/composables/community';
import { useCommunityTranslations } from '@/shared/composables/community/translations';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useViewport } from '@/shared/composables/useViewport';
import {
  getEntityFromContext,
  getEntityIdFromContext,
  getLanguageFromContext
} from '@/shared/utils/gateway/context';

import PostComposer from '../PostComposer.vue';
import YarnyCameraSvg from '../svgs/YarnyCamera.vue';
import PhotoGalleriesDesktop from './partials/PhotoGalleriesDesktop.vue';
import PhotoGalleriesMobile from './partials/PhotoGalleriesMobile.vue';
import PhotoGalleryLogic from './partials/PhotoGalleryLogic.vue';

const fallbackImageUrl = window.Hobbii?.fallbackImageUrl;

const { profile, initLogin } = useCommunityAuth();

const { contentModel, reset: resetContentModel } = useContentModel();

const { t } = useTranslations();
const communityTranslations = useCommunityTranslations('UserGalleris');
const { isMobile } = useViewport();

const {
  loading,
  processing,
  error,
  posts,
  hasMore,
  loadMore,
  total,
  createPost: createPostMutation,
  updatePost: updatePosMutation,
  deletePost: deletePostMutation
} = useCommunityPosts(
  {
    entity: getEntityFromContext(),
    entity_id: getEntityIdFromContext(),
    kind: KindType.Gallery,
    language: getLanguageFromContext()
  },
  { limit: 18 }
);

const activeGalleryIndex = ref<number>(0);

const prevPost = computed<Post | null>(
  () => posts.value[activeGalleryIndex.value - 1] ?? null
);
const activePost = computed<Post | null>(
  () => posts.value[activeGalleryIndex.value] ?? null
);
const nextPost = computed<Post | null>(
  () => posts.value[activeGalleryIndex.value + 1] ?? null
);

const getParametrizedFocalImage = (post: Post) => {
  const imageUrl = post.images?.[0];
  if (!imageUrl) {
    return '';
  }

  const url = new URL(imageUrl);
  url.searchParams.set('fit', 'max');
  url.searchParams.set('fm', 'jpg');
  url.searchParams.set('w', '380');
  url.searchParams.set('q', '60');
  return url.toString();
};

const showUserGallery = ref(false);
const openUserGallery = (index: number) => {
  activeGalleryIndex.value = index;
  showUserGallery.value = true;
};

const showComposer = ref(false);
const openComposer = () => {
  if (!profile.value) {
    return initLogin();
  }

  showComposer.value = true;
};

const createPost = async (model: ContentModel) => {
  if (!profile.value) {
    return initLogin();
  }

  await createPostMutation(model);
  showComposer.value = false;
  resetContentModel();
};

const prevGallery = () => {
  if (activeGalleryIndex.value > 0) {
    activeGalleryIndex.value--;
  }
};

const hasNextLoadedGallery = (index: number) => index < posts.value.length - 1;

const nextGallery = async () => {
  if (hasNextLoadedGallery(activeGalleryIndex.value)) {
    activeGalleryIndex.value++;
  } else {
    if (!loading.value && hasMore.value) {
      await loadMore();
      return nextGallery();
    }
  }
};

// prefetch additional posts when getting close to the end of the already loaded Posts.
const stopActiveGalleryIndexWatch = watch(
  activeGalleryIndex,
  (currentIndex) => {
    if (loading.value) {
      return;
    }

    if (!hasMore.value) {
      // no need to watch anymore
      stopActiveGalleryIndexWatch();
      return;
    }

    // will fetch more if within a certain end-threshold of the loaded posts.
    const threshold = currentIndex > posts.value.length - 3;
    if (threshold) {
      loadMore();
    }
  }
);

const reactionTranslations = {
  cool: t('community.shared.reactions.cool'),
  love: t('community.shared.reactions.love'),
  in_love: t('community.shared.reactions.in_love'),
  haha: t('community.shared.reactions.haha'),
  wow: t('community.shared.reactions.wow'),
  smile: t('community.shared.reactions.smile')
};
</script>

<template>
  <div>
    <Transition name="fade">
      <div v-if="loading" class="relative h-20">
        <LoadingSpinner :show="loading && !posts.length" />
      </div>
    </Transition>

    <Modal v-model:active="showComposer" size="md">
      <PostComposer
        v-if="profile"
        v-model="contentModel"
        :placeholder="
          communityTranslations.text_post_placeholder_personal(
            profile.display_name
          )
        "
        :processing="processing"
        :min-images="1"
        :max-images="10"
        :body-max-length="700"
        :community-translations="communityTranslations"
        @submit="createPost"
      />
    </Modal>

    <div v-if="error">
      <b>{{ t('global.error') }}: </b>{{ error.message }}
    </div>
    <div
      v-else-if="!loading && !posts.length"
      class="mt-5 flex flex-col items-center gap-5"
    >
      <h1 class="text-xl font-bold text-primary-dark">
        {{ communityTranslations.text_first_post }}
      </h1>
      <YarnyCameraSvg class="w-1/2 max-w-48" />
      <ButtonText
        size="lg"
        :label="communityTranslations.text_post"
        :icon-left="true"
        @click="openComposer"
      >
        <template #icon><IconImages class="text-xl text-white" /></template>
        {{ communityTranslations.text_post }}
      </ButtonText>
    </div>
    <div v-else-if="posts.length" class="relative">
      <div class="relative my-4 flex-col">
        <div class="mb-6">
          <h3 class="mb-1 text-lg font-bold text-black sm:mb-2 md:text-xl">
            {{ communityTranslations.text_heading }}
          </h3>
          <p class="mb-4">
            {{ communityTranslations.text_description }}
          </p>
          <ButtonText
            size="lg"
            :label="communityTranslations.text_post"
            :icon-left="true"
            class="w-full sm:w-auto"
            @click="openComposer"
          >
            <template #icon><IconImages class="text-xl text-white" /></template
            >{{ communityTranslations.text_post }}</ButtonText
          >
        </div>

        <UserGalleryGrid v-if="posts.length">
          <template v-for="(post, index) in posts" :key="post.id">
            <UserGalleryGridItem
              v-if="post.images"
              :image-url="getParametrizedFocalImage(post)"
              :total-image-count="post.images.length"
              :fallback-image-url="fallbackImageUrl"
              @click="openUserGallery(index)"
            />
          </template>
        </UserGalleryGrid>
      </div>
      <Transition name="fade">
        <div v-if="loading" class="relative h-20">
          <LoadingSpinner :show="loading" />
        </div>
      </Transition>

      <div v-if="hasMore" class="flex justify-center">
        <ButtonText @click="loadMore">
          {{ t('global.load_more') }}
        </ButtonText>
      </div>
    </div>

    <LightBox v-model:active="showUserGallery" close-label="Close">
      <LoadingSpinner
        :show="loading && !hasNextLoadedGallery(activeGalleryIndex)"
        theme="dark"
        class="text-7xl text-primary"
      />
      <PhotoGalleriesMobile
        v-if="isMobile"
        :prev-image="prevPost?.images?.[0] ?? null"
        :next-image="nextPost?.images?.[0] ?? null"
        :total-number-of-posts="total"
        :active-gallery-index="activeGalleryIndex"
        @prev-gallery="prevGallery"
        @next-gallery="nextGallery"
      >
        <PhotoGalleryLogic
          v-if="activePost"
          :post="activePost"
          :processing="processing"
          :create-post-mutation="createPostMutation"
          :update-post-mutation="updatePosMutation"
          :delete-post-mutation="deletePostMutation"
          :reaction-translations="reactionTranslations"
          :is-mobile="true"
        />
      </PhotoGalleriesMobile>
      <PhotoGalleriesDesktop
        v-else
        label-next="Next"
        label-prev="Previous"
        @prev-gallery="prevGallery"
        @next-gallery="nextGallery"
      >
        <PhotoGalleryLogic
          v-if="activePost"
          :post="activePost"
          :processing="processing"
          :create-post-mutation="createPostMutation"
          :update-post-mutation="updatePosMutation"
          :delete-post-mutation="deletePostMutation"
          :reaction-translations="reactionTranslations"
          :is-mobile="false"
        />
      </PhotoGalleriesDesktop>
    </LightBox>
  </div>
</template>
